import { motion } from "framer-motion";
import ScrollReveal from "./scroll-reveal";
import { useState, useEffect, useCallback } from "react";
import { useSwipeable } from "react-swipeable";

const testimonials = [
  {
    id: 1,
    name: "Rajesh Patel",
    company: "TechCorp Solutions",
    position: "CEO",
    content: "KASA Furniture transformed our office completely. The quality and design exceeded our expectations. Our employees love the new workspace!",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150",
  },
  {
    id: 2,
    name: "Priya Sharma",
    company: "Design Studio Inc",
    position: "Creative Director",
    content: "The attention to detail and craftsmanship is exceptional. Every piece feels premium and the ergonomic design has improved our team's productivity.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b372?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150",
  },
  {
    id: 3,
    name: "Amit Kumar",
    company: "StartupHub",
    position: "Founder",
    content: "Professional service from consultation to installation. KASA Furniture helped us create a workspace that reflects our company's vision and values.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150",
  },
];

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAutoPlaying && !isHovered) {
      interval = setInterval(nextSlide, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, isHovered, nextSlide]);

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <section className="py-20 bg-gradient-to-br from-accent/10 to-secondary dark:from-accent/5 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-neutral max-w-2xl mx-auto">
              Trusted by hundreds of businesses across India
            </p>
          </div>
        </ScrollReveal>

        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          {...handlers}
        >
          <div className="flex transition-transform duration-500 ease-in-out"
               style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.id} className="w-full flex-shrink-0 px-4">
                <ScrollReveal delay={index * 0.2}>
                  <motion.div
                    className="glass-effect backdrop-blur-lg rounded-2xl p-6 hover-lift hover-cursor"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="fas fa-star text-yellow-400 text-sm mr-1"></i>
                      ))}
                    </div>
                    
                    <p className="text-neutral dark:text-gray-300 mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    
                    <div className="flex items-center">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <h4 className="font-semibold text-primary dark:text-white">
                          {testimonial.name}
                        </h4>
                        <p className="text-sm text-neutral dark:text-gray-400">
                          {testimonial.position}, {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </ScrollReveal>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 p-2 rounded-full shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
            aria-label="Previous testimonial"
          >
            <i className="fas fa-chevron-left text-accent"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 p-2 rounded-full shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
            aria-label="Next testimonial"
          >
            <i className="fas fa-chevron-right text-accent"></i>
          </button>

          {/* Auto-play Toggle */}
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="absolute bottom-4 right-4 bg-white/80 dark:bg-gray-800/80 p-2 rounded-full shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
            aria-label={isAutoPlaying ? "Pause auto-play" : "Start auto-play"}
          >
            <i className={`fas ${isAutoPlaying ? 'fa-pause' : 'fa-play'} text-accent`}></i>
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}